import {
  DeductionShipment,
  DeductionShipments,
  OrderShipments,
  ShipmentCustomField,
  ShipmentFile,
  carrier_label,
  fetch_deduction_shipments,
  format_file_size,
  shipment_headline,
} from "@/api/dispute.tsx"
import { useAsyncEffect } from "@/utils"
import { ChevronDown, ChevronRight, ExternalLink, FileText, Image, Package, Truck } from "lucide-react"
import { useState } from "preact/hooks"

interface ShipmentPanelProps {
  deduction_id: string
  po_number: string | null | undefined
  className?: string
  /** Start expanded when NetSuite has a shipment (the detail page); collapsed otherwise. */
  default_open?: boolean
}

function Row({ label, value }: { label: string; value: string | number | null | undefined }) {
  if (value === null || value === undefined || value === "") return null
  return (
    <div class="flex gap-2 text-sm">
      <span class="w-36 shrink-0 text-slate-500">{label}</span>
      <span class="text-slate-800 whitespace-pre-line break-words">{value}</span>
    </div>
  )
}

function CustomFields({ fields }: { fields: ShipmentCustomField[] }) {
  return (
    <>
      {fields.map((f: ShipmentCustomField) => (
        <Row key={f.key} label={f.label} value={f.value} />
      ))}
    </>
  )
}

function NetSuiteLink({ url, label }: { url: string | null; label: string }) {
  if (!url) return <span>{label}</span>
  return (
    <a href={url} target="_blank" rel="noreferrer" class="inline-flex items-center gap-1 text-blue-700 hover:underline">
      {label}
      <ExternalLink className="h-3 w-3" />
    </a>
  )
}

/** One item fulfillment: how it went out, every package, and the fields the warehouse filled in. */
export function FulfillmentDetails({ shipment }: { shipment: DeductionShipment }) {
  const has_packages = shipment.packages.length > 0
  return (
    <div class="flex flex-col gap-1.5">
      <div class="flex items-center gap-2 text-sm font-medium text-slate-800">
        <Truck className="h-4 w-4 shrink-0 text-slate-600" />
        <span>{shipment_headline(shipment)}</span>
        {shipment.fulfillment_number ? (
          <span class="text-xs font-normal text-slate-500">
            · <NetSuiteLink url={shipment.url} label={`Fulfillment ${shipment.fulfillment_number}`} />
          </span>
        ) : null}
      </div>
      <div class="flex flex-col gap-0.5 pl-6">
        <Row label="Status" value={shipment.status} />
        <Row label="Carrier" value={shipment.carrier ? carrier_label(shipment.carrier) : null} />
        <Row label="Memo / freight ref" value={shipment.memo} />
        <Row label="Total weight" value={shipment.total_weight ? `${shipment.total_weight} lb` : null} />
        <Row label="Ship to" value={shipment.ship_to} />
        <CustomFields fields={shipment.custom_fields} />
        {has_packages ? (
          <div class="mt-1">
            <div class="text-xs font-medium uppercase tracking-wide text-slate-500 mb-1">Packages</div>
            <table class="text-xs w-full">
              <tbody>
                {shipment.packages.map((p, i) => {
                  const link = shipment.tracking.find(t => t.number === p.tracking_number)
                  return (
                    <tr key={i} class="border-t border-slate-100">
                      <td class="py-0.5 pr-2 text-slate-500">{i + 1}</td>
                      <td class="py-0.5 pr-2 font-mono">
                        {p.tracking_number ? (
                          link?.url ? (
                            <a href={link.url} target="_blank" rel="noreferrer" class="text-blue-700 hover:underline">
                              {p.tracking_number}
                            </a>
                          ) : (
                            p.tracking_number
                          )
                        ) : (
                          p.description ?? "—"
                        )}
                      </td>
                      <td class="py-0.5 pr-2 text-slate-600">{p.weight ? `${p.weight} lb` : ""}</td>
                      <td class="py-0.5 pr-2 text-slate-600">{p.dimensions ?? ""}</td>
                      <td class="py-0.5 text-slate-600">{p.delivery_confirmation ?? ""}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        ) : shipment.tracking.length > 0 ? (
          <Row
            label="Tracking"
            value={shipment.tracking.map(t => t.number).join(", ")}
          />
        ) : null}
      </div>
    </div>
  )
}

function FileRow({ file }: { file: ShipmentFile }) {
  const is_image = (file.filetype ?? "").toUpperCase().includes("IMAGE") || /\.(jpe?g|png|heic)$/i.test(file.name)
  const Icon = is_image ? Image : FileText
  const size = format_file_size(file.filesize)
  return (
    <div class="flex items-center gap-2 text-sm">
      <Icon className="h-3.5 w-3.5 shrink-0 text-slate-500" />
      {file.open_url ? (
        <a href={file.open_url} target="_blank" rel="noreferrer" class="text-blue-700 hover:underline break-all">
          {file.name}
        </a>
      ) : (
        <span class="break-all">{file.name}</span>
      )}
      <span class="text-xs text-slate-500 shrink-0">
        {[size, file.createddate].filter(Boolean).join(" · ")}
      </span>
    </div>
  )
}

/** One sales order with its fulfillments and files. */
export function OrderDetails({ order }: { order: OrderShipments }) {
  const o = order.order
  return (
    <div class="flex flex-col gap-3">
      <div class="flex flex-col gap-0.5">
        <div class="flex items-center gap-2 text-sm font-medium text-slate-800">
          <Package className="h-4 w-4 shrink-0 text-slate-600" />
          <NetSuiteLink url={o.url} label={`Sales order ${o.number ?? o.id}`} />
          {o.status ? <span class="text-xs font-normal text-slate-500">· {o.status}</span> : null}
        </div>
        <div class="flex flex-col gap-0.5 pl-6">
          <Row label="Customer" value={o.customer} />
          <Row label="Customer PO" value={o.po_number} />
          <Row label="Order date" value={o.order_date} />
          <Row label="Requested delivery" value={o.requested_delivery_date} />
          <Row label="Planned ship date" value={o.ship_date} />
          <Row label="Actual ship date" value={o.actual_ship_date} />
          <Row label="Ship method" value={o.ship_method && o.ship_method !== "Custom" ? o.ship_method : null} />
          <Row label="Ships from" value={o.location} />
          <Row label="Ship to" value={o.ship_to} />
          <Row label="Order memo" value={o.memo} />
          <Row label="Order total" value={o.total !== null ? `$${o.total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : null} />
          <CustomFields fields={o.custom_fields} />
        </div>
      </div>
      {order.fulfillments.map((f: DeductionShipment) => (
        <FulfillmentDetails key={f.fulfillment_id} shipment={f} />
      ))}
      {order.files.length > 0 ? (
        <div>
          <div class="text-xs font-medium uppercase tracking-wide text-slate-500 mb-1">Files in NetSuite</div>
          <div class="flex flex-col gap-1">
            {order.files.map((f: ShipmentFile) => (
              <FileRow key={f.id} file={f} />
            ))}
          </div>
          <div class="text-xs text-slate-500 mt-1">
            Opens in NetSuite (sign-in required). NetSuite's API does not release File Cabinet contents, so these are linked, not copied.
          </div>
        </div>
      ) : null}
    </div>
  )
}

/** One-line summary for the collapsed header. */
export function shipment_panel_summary(data: DeductionShipments): string {
  if (data.shipments.length === 0) return data.note ?? "No shipment found in NetSuite"
  const first = data.shipments[0]
  const more = data.shipments.length > 1 ? ` (+${data.shipments.length - 1} more)` : ""
  const files = data.orders.reduce((n: number, o: OrderShipments) => n + o.files.length, 0)
  return `${shipment_headline(first)}${more}${files ? ` · ${files} file${files === 1 ? "" : "s"}` : ""}`
}

/**
 * "Shipment" — what the brand's NetSuite says about the order behind this deduction: the sales
 * order, how and when it shipped, every package's tracking number, the freight reference, and the
 * files (pallet photos, POD, BOL) saved against it. Hidden when the org has no NetSuite connection
 * or the deduction has no PO.
 */
export function ShipmentPanel({ deduction_id, po_number, className, default_open = false }: ShipmentPanelProps) {
  const [data, setData] = useState<DeductionShipments | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [open, setOpen] = useState(false)

  useAsyncEffect(async () => {
    setData(null)
    setError(null)
    if (!po_number) return
    const res = await fetch_deduction_shipments(deduction_id)
    if (res.ok) {
      setData(res.value.data)
      setOpen(default_open && res.value.data.shipments.length > 0)
    } else {
      setError(res.error.message)
    }
  }, [deduction_id, po_number])

  if (!po_number) return null
  if (error) {
    return (
      <div class={`border border-slate-200 rounded-lg px-4 py-2.5 text-sm text-slate-600 ${className ?? ""}`}>
        <Truck className="inline h-4 w-4 mr-2 text-slate-500" />
        Could not read the shipment from NetSuite: {error}
      </div>
    )
  }
  if (!data) {
    return (
      <div class={`border border-slate-200 rounded-lg px-4 py-2.5 text-sm text-slate-500 ${className ?? ""}`}>
        <Truck className="inline h-4 w-4 mr-2 text-slate-400" />
        Looking up PO {po_number} in NetSuite…
      </div>
    )
  }
  if (!data.netsuite_connected) return null

  const Chevron = open ? ChevronDown : ChevronRight
  const found = data.shipments.length > 0
  return (
    <div class={`rounded-lg overflow-hidden border ${found ? "border-t-6 border-t-sky-500 border-sky-200 bg-white" : "border-slate-200"} ${className ?? ""}`}>
      <button
        type="button"
        class={`w-full flex items-center gap-2 px-4 py-2.5 text-left transition-colors ${found ? "bg-sky-50 hover:bg-sky-100 border-b border-sky-200" : "bg-slate-50 hover:bg-slate-100 border-b border-slate-200"}`}
        onClick={() => setOpen((o: boolean) => !o)}
        aria-expanded={open}>
        <Truck className={`h-4 w-4 shrink-0 ${found ? "text-sky-700" : "text-slate-600"}`} />
        <div class="min-w-0 flex-1">
          <div class="text-sm font-medium text-slate-800">How this order shipped · PO {po_number}</div>
          <div class="text-xs text-slate-500 truncate">{shipment_panel_summary(data)}</div>
        </div>
        <Chevron className="h-4 w-4 text-slate-500 shrink-0" />
      </button>
      {open ? (
        <div class="px-4 py-3 flex flex-col gap-4">
          {data.orders.length === 0 ? <div class="text-sm text-slate-600">{data.note}</div> : null}
          {data.orders.map((o: OrderShipments) => (
            <OrderDetails key={o.order.id} order={o} />
          ))}
        </div>
      ) : null}
    </div>
  )
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest
  const shipment: DeductionShipment = {
    sales_order_id: "1", sales_order_number: "3623969", po_number: "0653946000", fulfillment_id: "2", fulfillment_number: "3553707",
    ship_date: "8/6/2026", status: "Shipped", ship_method: "UPS® Ground", memo: "UPS", carrier: "ups", tracking_numbers: ["1Z"],
    package_count: 9, packages: [], ship_to: null, total_weight: 150, custom_fields: [], url: null, tracking: [],
  }
  const order: OrderShipments = {
    order: { id: "1", number: "3623969", po_number: "0653946000", customer: null, order_date: null, status: null, requested_delivery_date: null, ship_date: null, actual_ship_date: null, ship_method: null, ship_to: null, location: null, memo: null, total: null, linked_tracking_numbers: [], custom_fields: [], url: "" },
    fulfillments: [shipment],
    files: [{ id: "9", name: "SO3623969 (1).JPG", filetype: "JPGIMAGE", filesize: "1", createddate: null, url: null, open_url: null }],
  }
  describe("shipment_panel_summary", () => {
    it("summarises the first fulfillment and counts files", () => {
      expect(shipment_panel_summary({ po_number: "0653946000", netsuite_connected: true, orders: [order], shipments: [shipment, shipment], message_snippet: "", note: null }))
        .toBe("Shipped 8/6/2026 via UPS® Ground · 9 packages (+1 more) · 1 file")
    })
    it("falls back to the note", () => {
      expect(shipment_panel_summary({ po_number: "1", netsuite_connected: true, orders: [], shipments: [], message_snippet: "", note: "nothing" })).toBe("nothing")
    })
  })
}
